import type { CSSProperties, ReactElement } from "react";

type TFinancialValueThresholdComparator = (value: number) => boolean;
type TFinancialValueThresholdStylesProducer = (value: number | null) => CSSProperties | null;

interface IFinancialValueThresholdColumnStylesProps {
  value: number | null;
  comparators: TFinancialValueThresholdComparator[];
  styles: TFinancialValueThresholdStylesProducer[];
  children: (value: number | null, styles?: CSSProperties | null) => ReactElement | null;
}

export default function FinancialValueThresholdColumnStyles({
  value,
  comparators,
  styles,
  children,
}: IFinancialValueThresholdColumnStylesProps): ReactElement | null {
  if (styles.length !== comparators.length) {
    return children(value);
  }

  if (styles.length === 0 && comparators.length === 0) {
    return children(value);
  }

  if (value === null) {
    return children(value);
  }

  const applyStyles = comparators.reduce<CSSProperties | null>(
    (generatedStyles: CSSProperties | null, comparator: TFinancialValueThresholdComparator, index: number) => {
      if (generatedStyles !== null) {
        return generatedStyles;
      }

      const produceStyles = styles[index];

      if (comparator(value) && typeof produceStyles === "function") {
        return produceStyles(value);
      }

      return generatedStyles;
    },
    null,
  );

  return children(value, applyStyles);
}
